import React, { FC, useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom';
import { Page } from '../../components';
import { Home as HomeIcon, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Drawer, IconButton, List, ListItem, ListItemText, useMediaQuery, useTheme } from '@mui/material';

const drawerWidth = 200;

const sidebarOptions = [
  { key: 'Employees', getLabel: () => 'Employees', showWhenNoEmployeeId: true, showWhenEmployeeId: true, isParent: true },
  { key: 'Draws', getLabel: () => 'Draws', showWhenNoEmployeeId: false, showWhenEmployeeId: true, isParent: false },
  { key: 'Deductions', getLabel: () => 'Deductions', showWhenNoEmployeeId: false, showWhenEmployeeId: true, isParent: false },
];

const DrawsAndDeductions: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const employeeId = useMemo(() => {
    const m1 = location.pathname.match(/\/draws-and-deductions\/employees\/(\d+)(?:\/(draws|deductions))?$/);
    return m1 ? m1[1] : undefined;
  }, [location.pathname]);
  
    const getRoute = (key: string) => {
      switch (key) {
        case 'Employees':
          return `/draws-and-deductions/employees`;
        case 'Draws':
          return employeeId ? `/draws-and-deductions/employees/${employeeId}/draws` : `/draws-and-deductions/employees`;
        case 'Deductions':
          return employeeId ? `/draws-and-deductions/employees/${employeeId}/deductions` : `/draws-and-deductions/employees`;
        default:
          return `/draws-and-deductions/${key.toLowerCase()}`;
      }
    };
    return (
        <Page title="Draws and Deductions">
          <Box sx={{ display: 'flex', width: '100%' }}>
            {(sidebarOpen || isMobile) && (
                <Drawer
                  variant={isMobile ? 'temporary' : 'persistent'}
                  anchor="left"
                  open={sidebarOpen}
                  onClose={() => setSidebarOpen(false)}
                  ModalProps={{ keepMounted: true }}
                  sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                      width: drawerWidth,
                      boxSizing: 'border-box',
                      height: '100vh',
                      position: 'sticky',
                      top: '64px', 
                      zIndex: 1,
                      overflowY: 'auto',
                    },
                  }}
                >

                {/* Home + close */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: 1,
                    py: 1,
                  }}
                >
                  <IconButton
                    component={NavLink}
                    to="/draws-and-deductions/employees"
                    size="small"
                    title="Home"
                    edge="start"
                  >
                    <HomeIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => setSidebarOpen(false)}
                    size="small"
                    title="Close sidebar"
                  >
                    <ChevronLeft />
                  </IconButton>
                </Box>

                <List>
                  {sidebarOptions
                    .filter(opt => (employeeId ? opt.showWhenEmployeeId : opt.showWhenNoEmployeeId))
                    .map(({ key, getLabel, isParent }) => (
                    <ListItem
                      button
                      key={key}
                      component={NavLink}
                      to={getRoute(key)}
                      activeClassName="Mui-selected"
                      onClick={() => isMobile && setSidebarOpen(false)}
                      sx={{
                        pl: isParent ? 1 : 3,
                        fontWeight: isParent ? 600 : 400,
                        fontSize: isParent ? '1rem' : '0.9rem',
                        backgroundColor: 'transparent',
                        '&.Mui-selected': {
                          backgroundColor: isParent ? 'rgba(0, 0, 0, 0.04)' : 'rgba(0, 0, 0, 0.12)',
                        },
                      }}
                    >
                      <ListItemText primary={getLabel()} />
                    </ListItem>
                  ))}
                </List>
              </Drawer>
            )}

            <Box
              component="main"
              sx={{
                flexGrow: 1,
                width: `calc(100% - ${
                  sidebarOpen && !isMobile ? drawerWidth : 0
                }px)`,
                px: { xs: 1, sm: 2 },
                pt: 2,
                transition: 'all 0.3s ease',
              }}
            >
              {/* collapsed toggle + home */}
              {!sidebarOpen && !isMobile && (
                <Box
                  sx={{
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <IconButton
                    component={NavLink}
                    to="/draws-and-deductions/employees"
                    size="small"
                    title="Home"
                    sx={{ mr: 1 }}
                  >
                    <HomeIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => setSidebarOpen(true)}
                    size="small"
                    title="Open sidebar"
                  >
                    <ChevronRight />
                  </IconButton>
                </Box>
                    )}
            {children}
            </Box>
          </Box>
      
        </Page>
    );
}

export default DrawsAndDeductions